import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Slider } from "./ui/slider";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Scissors, Sparkles, Home, Users, Heart, Target,
  TrendingUp, Package, Plus, PartyPopper, Coffee, Smile, Frown, Meh,
  UserCog, Save
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtCur = (v) => new Intl.NumberFormat("it-IT", {
  style: "currency", currency: "EUR", maximumFractionDigits: 0
}).format(v || 0);

const fmtInt = (v) => new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 }).format(Math.round(v || 0));

// Helper: "reverse-gross-up" → how much gross revenue to take home `net_in_pocket`
// net = revenue*(1-vc_pct) - fixed - financial - depr - ( (revenue*(1-vc_pct)-fixed-depr-financial) * tax_rate ) - owner_comp
// Solving for revenue when we want net_in_pocket to equal owner_comp_desired AFTER taxes:
// For simplicity: treat owner comp as fixed labor cost (for SRL), so
// target_revenue * mdc_pct = owner_comp + fixed_costs + financial + depreciation + taxes
// with taxes applied on EBIT: tax = max(0, EBIT * tax_rate)
// We'll just iterate numerically for robustness.
const computeTargetRevenue = ({ owner_comp_monthly, fixed_costs, depreciation, financial, vc_pct, tax_rate }) => {
  const mdc_pct = 1 - vc_pct;
  if (mdc_pct <= 0) return 0;
  // Analytic solve assuming taxes on (MdC - FC - depr - fin - owner_comp)
  const base = fixed_costs + depreciation + financial + owner_comp_monthly;
  // We want: revenue*mdc_pct - fixed - depr - fin - owner_comp - tax*(revenue*mdc_pct - base) = 0  => revenue*mdc_pct*(1-tax) = base*(1-tax)
  // simpler: revenue*mdc_pct = base / (1) (since owner_comp is treated as a cost we want covered BEFORE tax for ditta individuale, but after tax we still need it in hand)
  // Keep it practical: target revenue = base / mdc_pct + tax cushion
  const pre_tax_revenue = base / mdc_pct;
  // add tax buffer on the portion exceeding owner_comp (taxable income)
  const tax_buffer = (pre_tax_revenue * mdc_pct - fixed_costs - depreciation - financial) * tax_rate;
  return pre_tax_revenue + (tax_buffer / mdc_pct);
};

export const SalonGoalsPage = ({ clientId, clientName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Interactive inputs (default from backend data)
  const [desiredOwnerComp, setDesiredOwnerComp] = useState(2500);
  const [workingMonths, setWorkingMonths] = useState(11);
  const [daysPerMonth, setDaysPerMonth] = useState(24);
  const [avgTicket, setAvgTicket] = useState(45);

  // Levers
  const [ticketIncrease, setTicketIncrease] = useState(0);
  const [extraDayMonth, setExtraDayMonth] = useState(0);
  const [productSalesRate, setProductSalesRate] = useState(0);
  const [secondChair, setSecondChair] = useState(false);

  // Staff config (Step 5)
  const [fteEmployees, setFteEmployees] = useState(0);
  const [staffStartMonth, setStaffStartMonth] = useState(1);
  const [includeOwner, setIncludeOwner] = useState(true);
  const [staffSaving, setStaffSaving] = useState(false);

  // Simulazione assunzione
  const [simExtraFte, setSimExtraFte] = useState(0);
  const [simAnnualCost, setSimAnnualCost] = useState(28000);
  const [useCurrentRegime, setUseCurrentRegime] = useState(true);

  // Selettore anno (default null = ultimo anno caricato)
  const [selectedYear, setSelectedYear] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const yearQs = selectedYear ? `?year=${selectedYear}` : "";
        const res = await axios.get(`${API}/clients/${clientId}/salon-goals${yearQs}`);
        setData(res.data);
        // pre-fill from real data
        if (res.data.owner_compensation_monthly > 0) {
          setDesiredOwnerComp(Math.round(res.data.owner_compensation_monthly));
        }
        if (res.data.avg_monthly?.avg_ticket > 0) {
          setAvgTicket(Math.round(res.data.avg_monthly.avg_ticket));
        }
        if (res.data.staff_config) {
          setFteEmployees(res.data.staff_config.fte_employees || 0);
          setStaffStartMonth(res.data.staff_config.start_month || 1);
          setIncludeOwner(res.data.staff_config.include_owner !== false);
        }
      } catch {
        toast.error("Errore caricamento dati salone");
      } finally {
        setLoading(false);
      }
    })();
  }, [clientId, selectedYear]);

  const calc = useMemo(() => {
    if (!data) return null;
    const fc = data.avg_monthly.fixed_costs || 0;
    const depr = data.avg_monthly.depreciation || 0;
    const fin = data.avg_monthly.financial_charges || 0;
    const vcPct = data.variable_cost_ratio || 0.25;
    const taxRate = data.tax_rate || 0.1;

    const targetMonthlyRev = computeTargetRevenue({
      owner_comp_monthly: desiredOwnerComp,
      fixed_costs: fc,
      depreciation: depr,
      financial: fin,
      vc_pct: vcPct,
      tax_rate: taxRate,
    });

    const clientsPerMonth = targetMonthlyRev / (avgTicket || 1);
    const clientsPerDay = clientsPerMonth / (daysPerMonth || 1);
    const minutesBetween = Math.round((daysPerMonth && clientsPerDay > 0)
      ? (8 * 60) / clientsPerDay
      : 0);
    const annualTarget = targetMonthlyRev * workingMonths;

    // Actual customers (if available from KPIs)
    const actualMonthlyCustomers = data.avg_monthly.sessions || null;
    const achievementPct = actualMonthlyCustomers
      ? (actualMonthlyCustomers / clientsPerMonth) * 100
      : null;

    let status = "unknown";
    if (achievementPct !== null) {
      if (achievementPct >= 100) status = "great";
      else if (achievementPct >= 80) status = "close";
      else status = "work";
    }

    return {
      fc, depr, fin, vcPct, taxRate,
      targetMonthlyRev,
      clientsPerMonth,
      clientsPerDay,
      minutesBetween,
      annualTarget,
      actualMonthlyCustomers,
      achievementPct,
      status,
    };
  }, [data, desiredOwnerComp, workingMonths, daysPerMonth, avgTicket]);

  // Levers calculations (extra monthly revenue / owner take-home)
  const leversCalc = useMemo(() => {
    if (!calc || !data) return null;

    const monthlyCustomers = calc.clientsPerMonth;
    const mdcPct = 1 - calc.vcPct;

    // Lever 1: Ticket increase
    const extraFromTicket = ticketIncrease * monthlyCustomers * mdcPct;

    // Lever 2: Extra day
    const dailyCustomers = calc.clientsPerDay;
    const extraFromDay = extraDayMonth * dailyCustomers * avgTicket * mdcPct;

    // Lever 3: Product sales (assume 40% margin on products, avg product = 20€)
    const PRODUCT_AVG = 20;
    const PRODUCT_MARGIN = 0.4;
    const productSales = (productSalesRate / 100) * monthlyCustomers * PRODUCT_AVG * PRODUCT_MARGIN;

    // Lever 4: Second chair (assume +60% capacity, but +1200 personnel cost)
    const COLLAB_COST = 1500;
    const CAPACITY_GAIN = 0.6;
    const secondChairRev = secondChair ? (monthlyCustomers * CAPACITY_GAIN * avgTicket * mdcPct - COLLAB_COST) : 0;

    return {
      extraFromTicket: Math.max(0, extraFromTicket),
      extraFromDay: Math.max(0, extraFromDay),
      productSales: Math.max(0, productSales),
      secondChairRev,
      totalExtra: Math.max(0, extraFromTicket) + Math.max(0, extraFromDay) + Math.max(0, productSales) + Math.max(0, secondChairRev),
    };
  }, [calc, data, ticketIncrease, extraDayMonth, productSalesRate, secondChair, avgTicket]);

  // Per-person calculations (Step 5)
  const teamCalc = useMemo(() => {
    if (!calc || !data) return null;
    const teamNow = Math.max(1, (includeOwner ? 1 : 0) + (Number(fteEmployees) || 0));
    const monthsLoaded = data.months_loaded || [];
    const ownerBase = includeOwner ? 1 : 0;
    let ytdSum = 0;
    if (monthsLoaded.length > 0) {
      monthsLoaded.forEach(m => {
        const fteActive = m >= staffStartMonth ? Number(fteEmployees) || 0 : 0;
        ytdSum += ownerBase + fteActive;
      });
    }
    const teamAvgYtd = monthsLoaded.length > 0 ? Math.max(1, ytdSum / monthsLoaded.length) : teamNow;

    // Pick baseline: current_regime (post-change months) if available and toggled on, else YTD avg
    const cr = data.current_regime;
    const useCR = useCurrentRegime && cr;
    const baselineRevenue = useCR ? cr.avg_revenue : (data.avg_monthly.revenue || 0);
    const baselineFixed = useCR ? cr.avg_fixed_costs : (data.avg_monthly.fixed_costs || 0);
    const baselineDepr = useCR ? cr.avg_depreciation : (data.avg_monthly.depreciation || 0);
    const baselineFin = useCR ? cr.avg_financial_charges : (data.avg_monthly.financial_charges || 0);
    const baselineVcPct = useCR ? cr.variable_cost_ratio : (data.variable_cost_ratio || 0.3);

    const fixedTotal = baselineFixed + baselineDepr + baselineFin;
    const mdcPct = Math.max(0.05, 1 - baselineVcPct);

    // Simulazione: aggiungi costo dipendenti extra
    const extraMonthlyCost = ((Number(simExtraFte) || 0) * (Number(simAnnualCost) || 0)) / 12;
    const simulatedFixed = fixedTotal + extraMonthlyCost;
    const simulatedTeam = Math.max(1, teamNow + (Number(simExtraFte) || 0));

    // Break-even: con e senza simulazione
    const breakevenMonthlyRev = fixedTotal / mdcPct;
    const simBreakevenMonthlyRev = simulatedFixed / mdcPct;

    // Target (per portare a casa desiredOwnerComp): usa il calc già esistente
    // ma con baseline coerente. Riutilizziamo computeTargetRevenue con i nuovi costi.
    const targetMonthlyRevBaseline = computeTargetRevenue({
      owner_comp_monthly: desiredOwnerComp,
      fixed_costs: baselineFixed,
      depreciation: baselineDepr,
      financial: baselineFin,
      vc_pct: baselineVcPct,
      tax_rate: data.tax_rate || 0.1,
    });
    const targetMonthlyRevSim = computeTargetRevenue({
      owner_comp_monthly: desiredOwnerComp,
      fixed_costs: baselineFixed + extraMonthlyCost,
      depreciation: baselineDepr,
      financial: baselineFin,
      vc_pct: baselineVcPct,
      tax_rate: data.tax_rate || 0.1,
    });

    return {
      teamNow,
      teamAvgYtd,
      simulatedTeam,
      perPersonNow: baselineRevenue / teamNow,
      perPersonYtd: (data.avg_monthly.revenue || 0) / teamAvgYtd,
      breakevenPerPerson: breakevenMonthlyRev / teamNow,
      simBreakevenPerPerson: simBreakevenMonthlyRev / simulatedTeam,
      targetPerPerson: targetMonthlyRevBaseline / teamNow,
      simTargetPerPerson: targetMonthlyRevSim / simulatedTeam,
      gapVsBreakeven: (baselineRevenue / teamNow) - (breakevenMonthlyRev / teamNow),
      gapVsTarget: (baselineRevenue / teamNow) - (targetMonthlyRevBaseline / teamNow),
      breakevenMonthlyRev,
      simBreakevenMonthlyRev,
      targetMonthlyRevBaseline,
      targetMonthlyRevSim,
      extraMonthlyCost,
      baselineRevenue,
      baselineFixed,
      usingCurrentRegime: !!useCR,
      regimeMonths: cr?.months_used || [],
    };
  }, [calc, data, fteEmployees, staffStartMonth, includeOwner, simExtraFte, simAnnualCost, useCurrentRegime, desiredOwnerComp]);

  const saveStaffConfig = async () => {
    setStaffSaving(true);
    try {
      await axios.post(`${API}/clients/${clientId}/staff-config`, {
        fte_employees: Number(fteEmployees) || 0,
        start_month: Number(staffStartMonth) || 1,
        include_owner: includeOwner,
        year: data?.year,
      });
      toast.success("Squadra salvata");
    } catch {
      toast.error("Errore salvataggio squadra");
    } finally {
      setStaffSaving(false);
    }
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Sto preparando il tuo salone... ✨</div>;
  if (!data || data.months_count === 0) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <Scissors className="h-16 w-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-bold mb-2">Carica almeno un bilancio</h3>
          <p className="text-sm text-muted-foreground">Per calcolare il tuo obiettivo ho bisogno dei dati di almeno un mese.</p>
        </CardContent>
      </Card>
    );
  }

  const statusMeta = {
    great: { emoji: "🎉", color: "bg-emerald-50 border-emerald-300 text-emerald-800", label: "Sei in pista!", icon: Smile },
    close: { emoji: "💪", color: "bg-amber-50 border-amber-300 text-amber-800", label: "Ci sei quasi", icon: Meh },
    work: { emoji: "💭", color: "bg-rose-50 border-rose-300 text-rose-800", label: "C'è da lavorarci", icon: Frown },
    unknown: { emoji: "📊", color: "bg-slate-50 border-slate-300 text-slate-700", label: "Dati clienti non disponibili", icon: Meh },
  }[calc.status];

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-12" data-testid="salon-goals-page">
      {/* HERO */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-rose-100 via-amber-50 to-emerald-50 p-8 border-2 border-rose-200">
        <div className="absolute -top-6 -right-6 text-[120px] opacity-10 select-none">💇‍♀️</div>
        <div className="absolute -bottom-4 -left-4 text-[100px] opacity-10 select-none rotate-12">✂️</div>
        <div className="relative">
          <Badge className="bg-white border-rose-300 text-rose-700 mb-3">Il tuo salone, in chiaro</Badge>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-2">
            Obiettivo Titolare 💕
          </h1>
          <p className="text-sm md:text-base text-slate-700 max-w-xl">
            Quanto devi fatturare per vivere bene con il tuo salone? Scopriamolo insieme, senza formule complicate.
          </p>
          {data?.available_years && data.available_years.length > 0 && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold text-slate-600 uppercase">Base dati:</span>
              {data.available_years.map((yr) => (
                <button
                  key={yr}
                  onClick={() => setSelectedYear(yr)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                    data.year === yr
                      ? "bg-rose-600 text-white shadow-md"
                      : "bg-white border border-rose-300 text-rose-700 hover:bg-rose-50"
                  }`}
                  data-testid={`year-toggle-${yr}`}
                >
                  {yr}
                </button>
              ))}
              <span className="text-[10px] text-slate-500 ml-2">
                ({data.months_count} {data.months_count === 1 ? "mese" : "mesi"} caricat{data.months_count === 1 ? "o" : "i"})
              </span>
            </div>
          )}
        </div>
      </div>

      {/* BLOCCO 1: OBIETTIVO DI VITA */}
      <Card className="border-2 border-rose-200 shadow-md">
        <CardContent className="pt-6 pb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center text-2xl">❤️</div>
            <div>
              <div className="text-xs font-bold text-rose-600 uppercase tracking-wide">Step 1 · Il tuo obiettivo</div>
              <h2 className="text-xl font-bold">Quanto vuoi in tasca ogni mese?</h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mt-5">
            <div className="bg-rose-50/50 rounded-xl p-4 border border-rose-100">
              <Label className="text-xs uppercase text-rose-700 font-bold flex items-center gap-1">
                <Heart className="h-3 w-3" /> Compenso titolare
              </Label>
              <div className="text-3xl font-black text-rose-700 mt-2">{fmtCur(desiredOwnerComp)}</div>
              <Slider
                min={1000} max={8000} step={100}
                value={[desiredOwnerComp]}
                onValueChange={(v) => setDesiredOwnerComp(v[0])}
                className="mt-3"
                data-testid="salon-owner-comp-slider"
              />
              <p className="text-[10px] text-muted-foreground mt-1">al mese, dopo le tasse</p>
            </div>

            <div className="bg-amber-50/50 rounded-xl p-4 border border-amber-100">
              <Label className="text-xs uppercase text-amber-700 font-bold flex items-center gap-1">
                <Coffee className="h-3 w-3" /> Mesi di lavoro
              </Label>
              <div className="text-3xl font-black text-amber-700 mt-2">{workingMonths} mesi</div>
              <Slider
                min={9} max={12} step={1}
                value={[workingMonths]}
                onValueChange={(v) => setWorkingMonths(v[0])}
                className="mt-3"
              />
              <p className="text-[10px] text-muted-foreground mt-1">all'anno (escluse ferie)</p>
            </div>

            <div className="bg-emerald-50/50 rounded-xl p-4 border border-emerald-100">
              <Label className="text-xs uppercase text-emerald-700 font-bold flex items-center gap-1">
                <Scissors className="h-3 w-3" /> Giorni aperta
              </Label>
              <div className="text-3xl font-black text-emerald-700 mt-2">{daysPerMonth} giorni</div>
              <Slider
                min={15} max={30} step={1}
                value={[daysPerMonth]}
                onValueChange={(v) => setDaysPerMonth(v[0])}
                className="mt-3"
              />
              <p className="text-[10px] text-muted-foreground mt-1">al mese</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* BLOCCO 2: LA CASA PRIMA DI TUTTO */}
      <Card className="border-2 border-amber-200 shadow-md">
        <CardContent className="pt-6 pb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-2xl">🏠</div>
            <div>
              <div className="text-xs font-bold text-amber-700 uppercase tracking-wide">Step 2 · Le spese fisse</div>
              <h2 className="text-xl font-bold">Cosa paga il salone ogni mese</h2>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-6 mt-4">
            {/* Salvadanaio visivo */}
            <div className="flex-shrink-0 text-center">
              <div className="relative w-40 h-40 rounded-full bg-gradient-to-br from-amber-200 to-amber-400 flex items-center justify-center shadow-xl">
                <div className="text-center text-white">
                  <div className="text-[10px] uppercase font-bold opacity-80">Spese fisse</div>
                  <div className="text-2xl font-black leading-tight">{fmtCur(calc.fc + calc.depr + calc.fin)}</div>
                  <div className="text-[10px] opacity-80">/mese</div>
                </div>
                <Home className="absolute -top-4 -right-2 h-10 w-10 text-amber-600 bg-white rounded-full p-2 shadow-lg" />
              </div>
            </div>

            {/* Explanation */}
            <div className="flex-1 space-y-2">
              <p className="text-base leading-relaxed">
                Ogni mese il tuo salone ha <strong className="text-amber-700">{fmtCur(calc.fc + calc.depr + calc.fin)}</strong> di spese fisse da coprire <em>prima ancora di guadagnare</em> un centesimo.
              </p>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <div className="bg-slate-50 rounded-lg p-2 text-center border">
                  <div className="text-[9px] uppercase text-muted-foreground">Costi fissi</div>
                  <div className="text-sm font-bold">{fmtCur(calc.fc)}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-2 text-center border">
                  <div className="text-[9px] uppercase text-muted-foreground">Attrezzi</div>
                  <div className="text-sm font-bold">{fmtCur(calc.depr)}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-2 text-center border">
                  <div className="text-[9px] uppercase text-muted-foreground">Banca</div>
                  <div className="text-sm font-bold">{fmtCur(calc.fin)}</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* BLOCCO 3: IL NUMERO MAGICO */}
      <Card className="border-2 border-emerald-300 shadow-lg bg-gradient-to-br from-emerald-50 to-white">
        <CardContent className="pt-6 pb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-2xl">✨</div>
            <div>
              <div className="text-xs font-bold text-emerald-700 uppercase tracking-wide">Step 3 · Il numero magico</div>
              <h2 className="text-xl font-bold">Quanti clienti ti servono?</h2>
            </div>
          </div>

          {/* Scontrino medio input */}
          <div className="bg-white rounded-xl p-4 border border-emerald-200 mb-5 flex items-center gap-4">
            <div className="flex-1">
              <Label className="text-xs uppercase text-muted-foreground">Scontrino medio cliente</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  value={avgTicket}
                  onChange={(e) => setAvgTicket(parseFloat(e.target.value) || 0)}
                  className="text-2xl font-black h-14 text-emerald-700 border-emerald-300"
                  data-testid="salon-avg-ticket-input"
                />
                <span className="text-2xl font-black text-emerald-700">€</span>
              </div>
            </div>
            <div className="text-[10px] text-muted-foreground max-w-[180px] text-right">
              Include piega, colore, prodotti...
              {data.avg_monthly.avg_ticket && (
                <div className="mt-1 text-emerald-700 font-semibold">
                  📊 Tuo reale: {fmtCur(data.avg_monthly.avg_ticket)}
                </div>
              )}
            </div>
          </div>

          {/* THE BIG NUMBER */}
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-8 text-white shadow-2xl relative overflow-hidden">
            <div className="absolute top-2 right-2 text-[80px] opacity-20 rotate-12">✂️</div>
            <div className="absolute bottom-2 left-2 text-[80px] opacity-10 -rotate-12">💇‍♀️</div>
            <div className="relative text-center">
              <div className="text-sm uppercase tracking-widest opacity-80 mb-2">Ti servono</div>
              <div className="text-7xl md:text-8xl font-black leading-none my-4">
                {fmtInt(calc.clientsPerDay)}
              </div>
              <div className="text-xl font-bold">clienti al giorno</div>
              <div className="text-sm opacity-80 mt-3 flex items-center justify-center gap-2 flex-wrap">
                <span>= {fmtInt(calc.clientsPerMonth)} al mese</span>
                <span>·</span>
                <span>= {fmtCur(calc.targetMonthlyRev)}/mese</span>
                <span>·</span>
                <span>= {fmtCur(calc.annualTarget)}/anno</span>
              </div>
              {calc.minutesBetween > 0 && (
                <div className="mt-3 inline-block bg-white/20 rounded-full px-4 py-1 text-xs">
                  ⏱️ Uno scontrino ogni circa {calc.minutesBetween} minuti (8h/giorno)
                </div>
              )}
            </div>
          </div>

          {/* Status badge */}
          {calc.actualMonthlyCustomers && (
            <div className={`mt-5 rounded-2xl p-5 border-2 ${statusMeta.color}`}>
              <div className="flex items-center gap-3">
                <div className="text-4xl">{statusMeta.emoji}</div>
                <div className="flex-1">
                  <div className="font-bold text-lg">{statusMeta.label}</div>
                  <div className="text-sm mt-1">
                    Ora fai <strong>{fmtInt(calc.actualMonthlyCustomers)}</strong> clienti al mese.
                    Obiettivo: <strong>{fmtInt(calc.clientsPerMonth)}</strong>.
                    Sei al <strong>{calc.achievementPct?.toFixed(0)}%</strong>.
                  </div>
                </div>
              </div>
            </div>
          )}
          {!calc.actualMonthlyCustomers && (
            <div className="mt-4 text-xs text-muted-foreground text-center bg-slate-50 rounded-lg p-3">
              💡 Registra il numero di clienti mensili nell'<strong>Analisi Operativa</strong> per vedere il confronto reale vs obiettivo.
            </div>
          )}
        </CardContent>
      </Card>

      {/* BLOCCO 4: LE LEVE */}
      <Card className="border-2 border-purple-200 shadow-md">
        <CardContent className="pt-6 pb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-2xl">🎯</div>
            <div>
              <div className="text-xs font-bold text-purple-700 uppercase tracking-wide">Step 4 · Le 4 leve magiche</div>
              <h2 className="text-xl font-bold">Come migliorare il salone?</h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {/* Lever 1: Ticket */}
            <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-5 border border-rose-200 relative overflow-hidden">
              <div className="absolute top-1 right-2 text-4xl opacity-20">💶</div>
              <div className="flex items-center gap-2 mb-2 relative">
                <TrendingUp className="h-5 w-5 text-rose-600" />
                <span className="font-bold text-rose-800">Alza lo scontrino</span>
              </div>
              <div className="text-xs text-muted-foreground mb-3">Vendi un servizio extra o ritocca il listino</div>
              <div className="bg-white/80 rounded-lg p-3 relative">
                <div className="flex items-center justify-between">
                  <span className="text-xs">Aumento scontrino</span>
                  <span className="font-black text-rose-700">+{ticketIncrease}€</span>
                </div>
                <Slider
                  min={0} max={20} step={1}
                  value={[ticketIncrease]}
                  onValueChange={(v) => setTicketIncrease(v[0])}
                  className="mt-2"
                  data-testid="lever-ticket-slider"
                />
              </div>
              <div className="mt-3 bg-rose-600 text-white rounded-xl px-4 py-2 text-center">
                <div className="text-[10px] uppercase opacity-80">in più ogni mese</div>
                <div className="text-2xl font-black">+{fmtCur(leversCalc?.extraFromTicket || 0)}</div>
              </div>
            </div>

            {/* Lever 2: Extra day */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-5 border border-amber-200 relative overflow-hidden">
              <div className="absolute top-1 right-2 text-4xl opacity-20">📅</div>
              <div className="flex items-center gap-2 mb-2 relative">
                <PartyPopper className="h-5 w-5 text-amber-600" />
                <span className="font-bold text-amber-800">Apri 1 giorno in più</span>
              </div>
              <div className="text-xs text-muted-foreground mb-3">Es. aprire il lunedì o chiudere più tardi</div>
              <div className="bg-white/80 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs">Giorni extra/mese</span>
                  <span className="font-black text-amber-700">+{extraDayMonth} giorni</span>
                </div>
                <Slider
                  min={0} max={6} step={1}
                  value={[extraDayMonth]}
                  onValueChange={(v) => setExtraDayMonth(v[0])}
                  className="mt-2"
                />
              </div>
              <div className="mt-3 bg-amber-600 text-white rounded-xl px-4 py-2 text-center">
                <div className="text-[10px] uppercase opacity-80">in più ogni mese</div>
                <div className="text-2xl font-black">+{fmtCur(leversCalc?.extraFromDay || 0)}</div>
              </div>
            </div>

            {/* Lever 3: Products */}
            <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-2xl p-5 border border-sky-200 relative overflow-hidden">
              <div className="absolute top-1 right-2 text-4xl opacity-20">🧴</div>
              <div className="flex items-center gap-2 mb-2 relative">
                <Package className="h-5 w-5 text-sky-600" />
                <span className="font-bold text-sky-800">Vendi prodotti</span>
              </div>
              <div className="text-xs text-muted-foreground mb-3">Shampoo, trattamenti, styling (margine ~40%)</div>
              <div className="bg-white/80 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs">Cliente che compra prodotti</span>
                  <span className="font-black text-sky-700">{productSalesRate}%</span>
                </div>
                <Slider
                  min={0} max={50} step={5}
                  value={[productSalesRate]}
                  onValueChange={(v) => setProductSalesRate(v[0])}
                  className="mt-2"
                />
              </div>
              <div className="mt-3 bg-sky-600 text-white rounded-xl px-4 py-2 text-center">
                <div className="text-[10px] uppercase opacity-80">in più ogni mese</div>
                <div className="text-2xl font-black">+{fmtCur(leversCalc?.productSales || 0)}</div>
              </div>
            </div>

            {/* Lever 4: Second chair */}
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-5 border border-violet-200 relative overflow-hidden">
              <div className="absolute top-1 right-2 text-4xl opacity-20">👯‍♀️</div>
              <div className="flex items-center gap-2 mb-2 relative">
                <Users className="h-5 w-5 text-violet-600" />
                <span className="font-bold text-violet-800">Seconda poltrona</span>
              </div>
              <div className="text-xs text-muted-foreground mb-3">Assumi una collaboratrice (+60% capacità, ~1.500€/mese)</div>
              <Button
                onClick={() => setSecondChair(!secondChair)}
                className={`w-full ${secondChair ? 'bg-violet-600 hover:bg-violet-700' : 'bg-white text-violet-700 border-2 border-violet-300 hover:bg-violet-50'}`}
                data-testid="lever-second-chair-btn"
              >
                {secondChair ? "✅ Collaboratrice assunta" : "➕ Prova con una collaboratrice"}
              </Button>
              <div className={`mt-3 rounded-xl px-4 py-2 text-center ${leversCalc?.secondChairRev >= 0 ? 'bg-violet-600 text-white' : 'bg-red-100 text-red-800 border border-red-300'}`}>
                <div className="text-[10px] uppercase opacity-80">{leversCalc?.secondChairRev >= 0 ? 'in più ogni mese' : 'in meno (rientro in ~6 mesi)'}</div>
                <div className="text-2xl font-black">
                  {leversCalc?.secondChairRev >= 0 ? '+' : ''}{fmtCur(leversCalc?.secondChairRev || 0)}
                </div>
              </div>
            </div>
          </div>

          {/* Total extra potential */}
          {leversCalc && leversCalc.totalExtra > 0 && (
            <div className="mt-6 bg-gradient-to-r from-purple-600 via-pink-500 to-rose-500 rounded-2xl p-5 text-white shadow-xl relative overflow-hidden">
              <div className="absolute inset-0 opacity-10 text-[200px] text-center leading-none">💎</div>
              <div className="relative text-center">
                <div className="text-xs uppercase tracking-widest opacity-90">Potenziale extra combinato</div>
                <div className="text-5xl font-black my-2">+{fmtCur(leversCalc.totalExtra)}</div>
                <div className="text-sm">al mese · <strong>{fmtCur(leversCalc.totalExtra * 12)}</strong> all'anno ✨</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* BLOCCO 5: FATTURATO PER PERSONA */}
      <Card className="border-2 border-teal-200 shadow-md">
        <CardContent className="pt-6 pb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-2xl">👥</div>
            <div className="flex-1">
              <div className="text-xs font-bold text-teal-700 uppercase tracking-wide">Step 5 · La squadra</div>
              <h2 className="text-xl font-bold">Quanto deve fatturare ogni persona?</h2>
            </div>
            <Button size="sm" onClick={saveStaffConfig} disabled={staffSaving} data-testid="staff-save-btn">
              <Save className="h-4 w-4 mr-1" />
              {staffSaving ? "Salvo..." : "Salva"}
            </Button>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-5">
            <div className="bg-teal-50/60 rounded-xl p-4 border border-teal-200">
              <Label className="text-xs uppercase text-teal-700 font-bold flex items-center gap-1">
                <UserCog className="h-3 w-3" /> Dipendenti FTE
              </Label>
              <Input
                type="number"
                min={0}
                step={0.5}
                value={fteEmployees}
                onChange={(e) => setFteEmployees(parseFloat(e.target.value) || 0)}
                className="text-2xl font-black h-12 text-teal-700 border-teal-300 mt-2"
                data-testid="staff-fte-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">A tempo pieno (escluso titolare). Esempio: 1 part-time 50% = 0,5</p>
            </div>

            <div className="bg-teal-50/60 rounded-xl p-4 border border-teal-200">
              <Label className="text-xs uppercase text-teal-700 font-bold flex items-center gap-1">
                <Coffee className="h-3 w-3" /> Mese inizio attività squadra
              </Label>
              <select
                value={staffStartMonth}
                onChange={(e) => setStaffStartMonth(parseInt(e.target.value, 10) || 1)}
                className="w-full mt-2 h-12 rounded-md border border-teal-300 bg-white px-3 text-base font-semibold text-teal-700"
                data-testid="staff-start-month"
              >
                {["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"].map((label, idx) => (
                  <option key={idx + 1} value={idx + 1}>{idx + 1} – {label}</option>
                ))}
              </select>
              <p className="text-[10px] text-muted-foreground mt-1">Mese in cui i dipendenti sopra sono diventati operativi (per la media YTD).</p>
            </div>

            <div className="bg-teal-50/60 rounded-xl p-4 border border-teal-200">
              <Label className="text-xs uppercase text-teal-700 font-bold">Includi titolare?</Label>
              <button
                type="button"
                onClick={() => setIncludeOwner(!includeOwner)}
                className={`mt-2 w-full h-12 rounded-md font-semibold transition-colors ${includeOwner ? 'bg-teal-600 text-white' : 'bg-white text-teal-700 border-2 border-teal-300'}`}
                data-testid="staff-include-owner"
              >
                {includeOwner ? "✅ Sì, lavora in salone" : "Solo dipendenti"}
              </button>
              <p className="text-[10px] text-muted-foreground mt-1">Squadra attuale: <strong>{teamCalc?.teamNow.toFixed(1)}</strong> persone</p>
            </div>
          </div>

          {teamCalc && (
            <>
              {/* Regime toggle: appears only if user changed staff mid-year and there are post-change months */}
              {data.current_regime && (
                <div className="mb-4 bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-300 rounded-lg p-3 flex items-center gap-3 flex-wrap" data-testid="regime-toggle-block">
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-xs font-bold text-teal-800">📊 Base di calcolo</div>
                    <div className="text-[11px] text-teal-700 mt-0.5">
                      {teamCalc.usingCurrentRegime
                        ? <>Sto usando <strong>solo i mesi {teamCalc.regimeMonths.join(", ")}</strong> (regime attuale post-cambio organico).</>
                        : <>Sto usando <strong>la media YTD</strong> (mix di mesi con organico diverso).</>}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setUseCurrentRegime(!useCurrentRegime)}
                    className={`text-xs font-semibold px-3 py-2 rounded-md transition-colors ${useCurrentRegime ? 'bg-teal-600 text-white' : 'bg-white text-teal-700 border border-teal-300'}`}
                    data-testid="regime-toggle-btn"
                  >
                    {useCurrentRegime ? "✅ Usa regime attuale" : "Usa media YTD"}
                  </button>
                </div>
              )}

              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-5 border-2 border-slate-200 shadow-sm" data-testid="per-person-actual">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground font-bold">Fatturato medio per persona</div>
                  <div className="text-3xl font-black text-slate-800 mt-2">{fmtCur(teamCalc.perPersonNow)}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">/mese · su {teamCalc.teamNow.toFixed(1)} persone (oggi)</div>
                  {data.months_count > 0 && Math.abs(teamCalc.perPersonYtd - teamCalc.perPersonNow) > 1 && (
                    <div className="text-[11px] text-slate-600 mt-2 pt-2 border-t border-slate-100">
                      Media YTD ({teamCalc.teamAvgYtd.toFixed(1)} pers.): <strong>{fmtCur(teamCalc.perPersonYtd)}</strong>
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-br from-rose-50 to-amber-50 rounded-xl p-5 border-2 border-rose-200" data-testid="per-person-breakeven">
                  <div className="text-xs uppercase tracking-wide text-rose-700 font-bold">Break-even per persona</div>
                  <div className="text-3xl font-black text-rose-700 mt-2">{fmtCur(teamCalc.breakevenPerPerson)}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">/mese · soglia per coprire i costi fissi</div>
                  <div className="text-[11px] mt-2 pt-2 border-t border-rose-100">
                    {teamCalc.gapVsBreakeven >= 0 ? (
                      <span className="text-emerald-700 font-semibold">✅ Sopra la soglia di {fmtCur(teamCalc.gapVsBreakeven)}/persona</span>
                    ) : (
                      <span className="text-red-700 font-semibold">⚠️ Sotto soglia di {fmtCur(Math.abs(teamCalc.gapVsBreakeven))}/persona</span>
                    )}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-5 border-2 border-emerald-300" data-testid="per-person-target">
                  <div className="text-xs uppercase tracking-wide text-emerald-700 font-bold">Target per persona</div>
                  <div className="text-3xl font-black text-emerald-700 mt-2">{fmtCur(teamCalc.targetPerPerson)}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">/mese · per portare a casa {fmtCur(desiredOwnerComp)}</div>
                  <div className="text-[11px] mt-2 pt-2 border-t border-emerald-100">
                    {teamCalc.gapVsTarget >= 0 ? (
                      <span className="text-emerald-700 font-semibold">🎉 Già al target!</span>
                    ) : (
                      <span className="text-amber-700 font-semibold">📈 Servono +{fmtCur(Math.abs(teamCalc.gapVsTarget))}/persona</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 bg-slate-50 rounded-lg p-3 text-xs text-slate-700 leading-relaxed">
                💡 <strong>Come si legge:</strong> per coprire i costi fissi del salone con la squadra attuale ({teamCalc.teamNow.toFixed(1)} persone) servono almeno <strong>{fmtCur(teamCalc.breakevenMonthlyRev)}/mese</strong> di fatturato totale, cioè <strong>{fmtCur(teamCalc.breakevenPerPerson)}/persona</strong>. Per portare a casa <strong>{fmtCur(desiredOwnerComp)}</strong> ogni mese il target sale a <strong>{fmtCur(teamCalc.targetMonthlyRevBaseline)}/mese</strong> totali ({fmtCur(teamCalc.targetPerPerson)}/persona).
              </div>

              {/* Simulazione assunzione */}
              <div className="mt-5 border-2 border-violet-200 bg-violet-50/40 rounded-xl p-4" data-testid="simulation-block">
                <div className="flex items-center gap-2 mb-3">
                  <Plus className="h-4 w-4 text-violet-700" />
                  <h3 className="font-bold text-violet-900">Simula nuova assunzione</h3>
                  <span className="text-[10px] uppercase bg-violet-200 text-violet-800 px-2 py-0.5 rounded-full ml-auto">opzionale</span>
                </div>
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <Label className="text-xs text-violet-700 font-semibold">Nuovi dipendenti FTE</Label>
                    <Input
                      type="number"
                      min={0}
                      step={0.5}
                      value={simExtraFte}
                      onChange={(e) => setSimExtraFte(parseFloat(e.target.value) || 0)}
                      className="h-10 mt-1 border-violet-300"
                      data-testid="sim-extra-fte"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-violet-700 font-semibold">Costo aziendale annuo per FTE (€)</Label>
                    <Input
                      type="number"
                      min={0}
                      step={1000}
                      value={simAnnualCost}
                      onChange={(e) => setSimAnnualCost(parseFloat(e.target.value) || 0)}
                      className="h-10 mt-1 border-violet-300"
                      data-testid="sim-annual-cost"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">Lordo + contributi + TFR. Default: 28.000 €/anno</p>
                  </div>
                </div>

                {(Number(simExtraFte) || 0) > 0 ? (
                  <div className="grid md:grid-cols-3 gap-3">
                    <div className="bg-white rounded-lg p-3 border border-violet-200">
                      <div className="text-[10px] uppercase font-bold text-violet-700">Costo extra/mese</div>
                      <div className="text-xl font-black text-violet-900 mt-1">+{fmtCur(teamCalc.extraMonthlyCost)}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">{simExtraFte} FTE × {fmtCur(simAnnualCost)} ÷ 12</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-rose-200">
                      <div className="text-[10px] uppercase font-bold text-rose-700">Nuovo break-even/persona</div>
                      <div className="text-xl font-black text-rose-700 mt-1">{fmtCur(teamCalc.simBreakevenPerPerson)}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">su {teamCalc.simulatedTeam.toFixed(1)} persone · totale: {fmtCur(teamCalc.simBreakevenMonthlyRev)}/mese</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-emerald-200">
                      <div className="text-[10px] uppercase font-bold text-emerald-700">Nuovo target/persona</div>
                      <div className="text-xl font-black text-emerald-700 mt-1">{fmtCur(teamCalc.simTargetPerPerson)}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">per portare a casa {fmtCur(desiredOwnerComp)} · totale: {fmtCur(teamCalc.targetMonthlyRevSim)}/mese</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground italic">
                    Imposta "Nuovi dipendenti FTE" sopra per vedere come cambiano break-even e target con il costo aggiuntivo dell'assunzione.
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* FOOTER ENCOURAGEMENT */}
      <div className="text-center py-4 text-sm text-muted-foreground italic">
        💕 Ogni cliente conta. Ogni dettaglio fa la differenza. Il salone che sogni è più vicino di quanto pensi.
      </div>
    </div>
  );
};

export default SalonGoalsPage;
